import React, { useState } from 'react';
import SearchableDropdown from '../../components/common/SearchableDropdown';
import { UserPlusIcon, PlusIcon, TrashIcon, XMarkIcon, AcademicCapIcon, UserGroupIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';

const AcademicLeadershipManager = ({
    schoolLevels,
    teachers,
    hosTeachers,
    principalTeachers = [],
    grades,
    onAddLevel,
    onUpdateLevel,
    onDeleteLevel,
    onToggleHOS,
    isSavingHOS,
    onTogglePrincipal,
    isSavingPrincipal
}) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingLevelId, setEditingLevelId] = useState(null);
    
    // Form state for creating a new department
    const [newDeptName, setNewDeptName] = useState('');
    const [newDeptGrades, setNewDeptGrades] = useState([]);
    const [newDeptHods, setNewDeptHods] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleCreateOrUpdateDepartment = async () => {
        if (!newDeptName || isSubmitting) return;
        setIsSubmitting(true);
        try {
            const data = {
                name: newDeptName,
                grades: newDeptGrades,
                hod_teachers: newDeptHods
            };
            
            const success = editingLevelId 
                ? await onUpdateLevel(editingLevelId, data)
                : await onAddLevel(data);
                
            if (success) {
                closeModal();
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleToggleGrade = (gradeId) => {
        setNewDeptGrades(prev => 
            prev.includes(gradeId) ? prev.filter(id => id !== gradeId) : [...prev, gradeId]
        );
    };

    const openEditModal = (level) => {
        setEditingLevelId(level.id);
        setNewDeptName(level.name);
        setNewDeptGrades(level.grades.map(g => g.id));
        setNewDeptHods(level.hod_teachers || []);
        setIsModalOpen(true);
    };

    const openCreateModal = () => {
        setEditingLevelId(null);
        setNewDeptName('');
        setNewDeptGrades([]);
        setNewDeptHods([]);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingLevelId(null);
        setNewDeptName('');
        setNewDeptGrades([]);
        setNewDeptHods([]);
    };

    // Derived HOS details for display
    const currentHosDetails = teachers.filter(t => hosTeachers.includes(t.id));

    return (
        <section className="bg-white rounded-[3rem] p-10 border border-slate-100 shadow-2xl shadow-indigo-900/5 mb-12">
            <div className="flex items-center justify-between mb-10 border-b border-slate-50 pb-6">
                <div>
                    <h2 className="text-2xl font-black text-[#18216D] tracking-tighter uppercase italic flex items-center gap-4">
                        <ShieldCheckIcon className="w-7 h-7 text-[#FFC425]" />
                        Academic Leadership
                    </h2>
                    <p className="text-xs font-black text-slate-400 uppercase tracking-[0.2em] mt-2">Manage Heads of Studies and Departments</p>
                </div>
                <button
                    onClick={openCreateModal}
                    className="px-8 py-4 bg-[#18216D] text-white rounded-2xl text-[10px] font-black uppercase tracking-widest shadow-xl shadow-indigo-900/20 hover:scale-105 transition-all flex items-center gap-2"
                >
                    <PlusIcon className="w-5 h-5" />
                    New Department
                </button>
            </div>

            {/* Visual Display */}
            <div className="space-y-12">
                {/* Leadership Display */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                    {/* HOS Display */}
                    <div>
                        <h3 className="text-xl font-black text-[#18216D] tracking-tighter uppercase mb-6 flex items-center gap-3">
                            <AcademicCapIcon className="w-6 h-6 text-indigo-400" />
                            Heads of Studies (HOS)
                        </h3>
                        <div className="bg-indigo-50/30 rounded-[2rem] p-6 border border-indigo-50">
                            <SearchableDropdown
                                options={teachers}
                                selectedValues={hosTeachers}
                                onChange={(teacherId) => {
                                    const isCurrentlyHOS = hosTeachers.includes(teacherId);
                                    onToggleHOS(teacherId, !isCurrentlyHOS);
                                }}
                                placeholder="Search and assign Head of Studies..."
                                isMultiple={true}
                                displayKey="name"
                                subDisplayKey="specialization"
                                disabled={isSavingHOS}
                            />
                        </div>
                    </div>

                    {/* Principal Display */}
                    <div>
                        <h3 className="text-xl font-black text-[#18216D] tracking-tighter uppercase mb-6 flex items-center gap-3">
                            <i className="fas fa-school text-amber-400 text-xl"></i>
                            Principal of the School
                        </h3>
                        <div className="bg-amber-50/30 rounded-[2rem] p-6 border border-amber-50">
                            <SearchableDropdown
                                options={teachers}
                                selectedValues={principalTeachers}
                                onChange={(teacherId) => {
                                    const isCurrentlyPrincipal = principalTeachers.includes(teacherId);
                                    onTogglePrincipal(teacherId, !isCurrentlyPrincipal);
                                }}
                                placeholder="Search and assign Principal..."
                                isMultiple={true}
                                displayKey="name"
                                subDisplayKey="specialization"
                                disabled={isSavingPrincipal}
                            />
                        </div>
                    </div>
                </div>

                {/* Departments Display */}
                <div>
                    <h3 className="text-xl font-black text-[#18216D] tracking-tighter uppercase mb-6 flex items-center gap-3">
                        <UserGroupIcon className="w-6 h-6 text-emerald-400" />
                        Departments & HODs
                    </h3>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {schoolLevels.map(level => (
                            <div key={level.id} className="bg-white rounded-[2rem] p-8 border-2 border-slate-50 hover:border-emerald-50 transition-colors shadow-sm relative overflow-hidden group">
                                <div className="absolute top-0 right-0 w-32 h-32 bg-emerald-50/50 rounded-bl-full -mr-16 -mt-16 transition-transform group-hover:scale-125"></div>
                                <div className="relative z-10 flex justify-between items-start mb-6">
                                    <h4 className="text-xl font-black text-[#18216D] tracking-tighter uppercase">{level.name}</h4>
                                    <div className="flex items-center gap-2">
                                        <button 
                                            onClick={() => openEditModal(level)}
                                            className="p-2 text-slate-300 hover:text-indigo-500 hover:bg-indigo-50 rounded-xl transition-colors"
                                            title="Edit Department"
                                        >
                                            <i className="fas fa-pen-to-square text-xs"></i>
                                        </button>
                                        <button 
                                            onClick={() => onDeleteLevel(level.id)}
                                            className="p-2 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-colors"
                                            title="Delete Department"
                                        >
                                            <TrashIcon className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>

                                <div className="relative z-10 space-y-6">
                                    <div>
                                        <label className="text-[9px] font-black text-slate-400 uppercase tracking-[0.2em] block mb-3">Assigned Grades</label>
                                        <div className="flex flex-wrap gap-2">
                                            {(level.grades || []).map(g => (
                                                <span key={g.id} className="px-3 py-1.5 bg-slate-50 text-slate-600 rounded-xl text-[9px] font-black uppercase tracking-wider border border-slate-100">{g.name}</span>
                                            ))}
                                            {(!level.grades || level.grades.length === 0) && <span className="text-[9px] text-slate-300 uppercase tracking-widest italic">None</span>}
                                        </div>
                                    </div>
                                    <div>
                                        <label className="text-[9px] font-black text-emerald-400 uppercase tracking-[0.2em] block mb-3">Heads of Department</label>
                                        <div className="flex flex-wrap gap-2">
                                            {(level.hod_teacher_names || []).map((name, i) => (
                                                <span key={i} className="px-4 py-2 bg-emerald-50 text-emerald-600 rounded-xl text-[10px] font-black uppercase tracking-widest">{name}</span>
                                            ))}
                                            {(!level.hod_teacher_names || level.hod_teacher_names.length === 0) && <span className="text-[9px] text-slate-300 uppercase tracking-widest italic">No HODs Assigned</span>}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                        {schoolLevels.length === 0 && (
                            <div className="col-span-full py-12 text-center bg-slate-50 rounded-3xl border border-dashed border-slate-200">
                                <p className="text-xs font-black text-slate-300 uppercase tracking-widest italic">No Departments Created</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Unified Assignment Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-[#18216D]/40 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white rounded-[3rem] w-full max-w-4xl max-h-[90vh] overflow-y-auto custom-scrollbar shadow-2xl animate-in zoom-in-95 duration-200 relative">
                        <button 
                            onClick={closeModal}
                            className="absolute top-6 right-6 p-4 text-slate-400 hover:text-[#18216D] hover:bg-slate-50 rounded-full transition-colors"
                        >
                            <XMarkIcon className="w-6 h-6 stroke-[3]" />
                        </button>

                        <div className="px-8 pt-8 pb-4 border-b border-slate-100 bg-slate-50/50 rounded-t-[3rem]">
                            <h2 className="text-2xl font-black text-[#18216D] tracking-tight uppercase italic">{editingLevelId ? 'Edit Department' : 'Create Department'}</h2>
                        </div>

                        <div className="p-10 space-y-12">
                            {/* Department Form */}
                            <section>
                                <div className="max-w-4xl mx-auto space-y-8">
                                    <div>
                                        <label className="text-[10px] font-black text-[#18216D] uppercase tracking-widest block mb-3">Department Name</label>
                                        <input
                                            type="text"
                                            placeholder="e.g. SENIOR SCHOOL"
                                            value={newDeptName}
                                            onChange={e => setNewDeptName(e.target.value)}
                                            className="w-full bg-white border border-slate-200 rounded-2xl px-6 py-4 text-sm font-black text-[#18216D] uppercase focus:ring-4 focus:ring-emerald-100 focus:border-emerald-300 shadow-sm transition-all"
                                        />
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                        <div>
                                            <label className="text-[10px] font-black text-[#18216D] uppercase tracking-widest block mb-3">Select Grades</label>
                                            <SearchableDropdown
                                                options={grades}
                                                selectedValues={newDeptGrades}
                                                onChange={(id) => {
                                                    handleToggleGrade(id);
                                                }}
                                                placeholder="Select Grades..."
                                                isMultiple={true}
                                                displayKey="name"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[10px] font-black text-[#18216D] uppercase tracking-widest block mb-3">Assign HODs</label>
                                            <SearchableDropdown
                                                options={teachers}
                                                selectedValues={newDeptHods}
                                                onChange={(id) => {
                                                    setNewDeptHods(prev => prev.includes(id) ? prev.filter(tid => tid !== id) : [...prev, id]);
                                                }}
                                                placeholder="Select HODs..."
                                                isMultiple={true}
                                                displayKey="name"
                                                subDisplayKey="specialization"
                                            />
                                        </div>
                                    </div>

                                    <div className="flex justify-end pt-6 border-t border-slate-100 gap-4">
                                        <button
                                            onClick={closeModal}
                                            className="px-8 py-4 bg-white text-slate-400 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all border border-slate-100 hover:text-[#18216D]"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            onClick={handleCreateOrUpdateDepartment}
                                            disabled={!newDeptName || isSubmitting}
                                            className={`px-8 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all shadow-xl flex items-center gap-2 ${
                                                !newDeptName || isSubmitting 
                                                ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                                : 'bg-[#FFC425] text-[#18216D] hover:scale-105 shadow-[#FFC425]/20'
                                            }`}
                                        >
                                            {isSubmitting ? 'Saving...' : (
                                                <>
                                                    <PlusIcon className="w-4 h-4 stroke-[3]" />
                                                    {editingLevelId ? 'Save Changes' : 'Create Department'}
                                                </>
                                            )}
                                        </button>
                                    </div>
                                </div>
                            </section>
                        </div>
                    </div>
                </div>
            )}
        </section>
    );
};

export default AcademicLeadershipManager;
